import { useState } from "react";
import {
  Paper,
  Group,
  Stack,
  Title,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Card,
  Checkbox,
  Modal,
  ScrollArea,
  CopyButton,
  ActionIcon,
  Tooltip,
  Code,
  Divider,
  Image,
  Anchor,
} from "@mantine/core";
import {
  IconDownload,
  IconCopy,
  IconCheck,
  IconArrowLeft,
  IconArrowRight,
  IconBraces,
  IconPhoto,
  IconExternalLink,
  IconSquareCheck,
  IconSquare,
} from "@tabler/icons-react";
import { filenameFor } from "./helpers";

function triggerDownload(name, content) {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function JsonReview({
  payload,
  filteredOut,
  selectedKeys,
  setSelectedKeys,
  onBack,
  onNext,
}) {
  const [jsonModalKey, setJsonModalKey] = useState(null);
  const [imagesModalKey, setImagesModalKey] = useState(null);

  const totalLines = payload.reduce(
    (s, g) => s + (g.gr_lines?.length || 0),
    0
  );
  const allSelected =
    payload.length > 0 && selectedKeys.size === payload.length;
  const noneSelected = selectedKeys.size === 0;

  const toggle = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const setAll = (on) => {
    setSelectedKeys(
      on ? new Set(payload.map((g) => g.transaction_key)) : new Set()
    );
  };

  if (payload.length === 0) {
    return (
      <Paper>
        <Text c="dimmed">
          No GR data was generated. Go back and adjust your filters or
          selection.
        </Text>
        <Group mt="md">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={onBack}
          >
            Back to filters
          </Button>
        </Group>
      </Paper>
    );
  }

  const jsonModalGr = jsonModalKey
    ? payload.find((g) => g.transaction_key === jsonModalKey)
    : null;
  const imagesModalGr = imagesModalKey
    ? payload.find((g) => g.transaction_key === imagesModalKey)
    : null;

  return (
    <Stack gap="md">
      <Paper>
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Group gap="xs" mb={4}>
              <Title order={4}>Review &amp; select GRs</Title>
              <Badge color="green" variant="light">
                {selectedKeys.size} / {payload.length} selected
              </Badge>
              <Badge variant="light">{totalLines} lines total</Badge>
              {filteredOut > 0 && (
                <Badge color="yellow" variant="light">
                  {filteredOut} excluded (fresh)
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              Tick the GRs you want to push. Use{" "}
              <strong>View JSON</strong> or <strong>View images</strong> on
              each card to inspect, then uncheck anything you don't want sent.
            </Text>
          </div>
          <Group>
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
            >
              Back
            </Button>
            <Button
              variant="subtle"
              leftSection={
                allSelected ? (
                  <IconSquare size={16} />
                ) : (
                  <IconSquareCheck size={16} />
                )
              }
              onClick={() => setAll(!allSelected)}
            >
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
            <Button
              rightSection={<IconArrowRight size={16} />}
              onClick={onNext}
              disabled={noneSelected}
            >
              Continue with {selectedKeys.size}
            </Button>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 4 }} spacing="md">
        {payload.map((g) => (
          <GRCard
            key={g.transaction_key}
            gr={g}
            selected={selectedKeys.has(g.transaction_key)}
            onToggle={() => toggle(g.transaction_key)}
            onViewJson={() => setJsonModalKey(g.transaction_key)}
            onViewImages={() => setImagesModalKey(g.transaction_key)}
          />
        ))}
      </SimpleGrid>

      <JsonPreviewModal
        gr={jsonModalGr}
        onClose={() => setJsonModalKey(null)}
      />
      <ImagesPreviewModal
        gr={imagesModalGr}
        onClose={() => setImagesModalKey(null)}
      />
    </Stack>
  );
}

function GRCard({ gr, selected, onToggle, onViewJson, onViewImages }) {
  const lineCount = gr.gr_lines?.length || 0;
  const imageCount = Array.isArray(gr.images_array) ? gr.images_array.length : 0;
  const supplierCode = gr.gr_header?.supplier_code;
  const supplierName = gr.gr_header?.supplier_name;

  return (
    <Card
      withBorder
      radius="md"
      padding="md"
      style={{
        borderColor: selected
          ? "var(--mantine-color-indigo-5)"
          : undefined,
        background: selected
          ? "var(--mantine-color-indigo-0)"
          : undefined,
        transition: "background-color 120ms ease, border-color 120ms ease",
      }}
    >
      <Stack gap="xs" h="100%">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Checkbox
            checked={selected}
            onChange={onToggle}
            aria-label={`Toggle ${gr.transaction_key}`}
          />
          <Group gap={4}>
            <Tooltip
              label={imageCount > 0 ? `${imageCount} image(s)` : "No images"}
            >
              <Badge
                variant={imageCount > 0 ? "light" : "default"}
                color={imageCount > 0 ? "blue" : "gray"}
                leftSection={<IconPhoto size={12} />}
              >
                {imageCount}
              </Badge>
            </Tooltip>
            <Tooltip label={`${lineCount} line(s)`}>
              <Badge variant="default">{lineCount} L</Badge>
            </Tooltip>
          </Group>
        </Group>

        <div>
          <Text ff="monospace" fw={700} size="sm" lineClamp={1}>
            {gr.transaction_key}
          </Text>
          <Group gap={6} mt={2} wrap="nowrap">
            {supplierCode && (
              <Badge size="xs" variant="light" ff="monospace">
                {supplierCode}
              </Badge>
            )}
            <Text size="xs" c="dimmed" lineClamp={1}>
              {supplierName || "—"}
            </Text>
          </Group>
        </div>

        <Group gap={4} mt="auto" grow>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPhoto size={14} />}
            disabled={imageCount === 0}
            onClick={onViewImages}
          >
            Image{imageCount === 1 ? "" : "s"}
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconBraces size={14} />}
            onClick={onViewJson}
          >
            JSON
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function JsonPreviewModal({ gr, onClose }) {
  if (!gr) return null;
  const json = JSON.stringify(gr, null, 2);
  const filename = filenameFor(gr);
  return (
    <Modal
      opened={!!gr}
      onClose={onClose}
      size="xl"
      radius="md"
      title={
        <Group gap="xs">
          <IconBraces size={18} />
          <Text ff="monospace">{filename}</Text>
          {gr.gr_header?.supplier_code && (
            <Badge variant="default" size="sm">
              {gr.gr_header.supplier_code}
            </Badge>
          )}
        </Group>
      }
    >
      <Stack gap="sm">
        <Group justify="flex-end" gap="xs">
          <CopyButton value={json} timeout={1500}>
            {({ copied, copy }) => (
              <Button
                variant="default"
                size="xs"
                leftSection={
                  copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                }
                onClick={copy}
                color={copied ? "green" : undefined}
              >
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            )}
          </CopyButton>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconDownload size={14} />}
            onClick={() => triggerDownload(filename, json)}
          >
            Download
          </Button>
        </Group>
        <ScrollArea.Autosize mah="65vh">
          <Code
            block
            fz="xs"
            style={{
              background: "var(--mantine-color-dark-8)",
              color: "var(--mantine-color-gray-1)",
              padding: "var(--mantine-spacing-md)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            {json}
          </Code>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  );
}

function ImagesPreviewModal({ gr, onClose }) {
  if (!gr) return null;
  const images = gr.images_array || [];
  return (
    <Modal
      opened={!!gr}
      onClose={onClose}
      size="xl"
      radius="md"
      title={
        <Group gap="xs">
          <IconPhoto size={18} />
          <Text ff="monospace">{gr.transaction_key}</Text>
          <Badge variant="light" size="sm">
            {images.length} image{images.length === 1 ? "" : "s"}
          </Badge>
        </Group>
      }
    >
      {images.length === 0 ? (
        <Text c="dimmed">No images attached to this GR.</Text>
      ) : (
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: images.length > 1 ? 2 : 1 }}>
            {images.map((url) => (
              <Stack key={url} gap={4}>
                <Anchor href={url} target="_blank" rel="noopener noreferrer">
                  <Image
                    src={url}
                    alt={`GR ${gr.transaction_key} attachment`}
                    radius="md"
                    fit="contain"
                    h={360}
                    fallbackSrc=""
                  />
                </Anchor>
                <Group gap={4} wrap="nowrap">
                  <Code fz="xs" style={{ flex: 1, wordBreak: "break-all" }}>
                    {url.split("/").slice(-2).join("/")}
                  </Code>
                  <Tooltip label="Open original in new tab">
                    <ActionIcon
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="default"
                      size="sm"
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Stack>
            ))}
          </SimpleGrid>
          <Divider />
          <Text size="xs" c="dimmed">
            Images are served from <Code>storage.googleapis.com/shini-images-prod</Code>.
            Click any image to open the original at full size in a new tab.
          </Text>
        </Stack>
      )}
    </Modal>
  );
}
